/**
 * Leviathan Web — "Lifted at" on a shipment line
 * Quasar Prime LLC
 *
 * Where the fuel on this line came from.
 *
 * Tax liability follows the MOVEMENT, not the delivery address: a load lifted
 * at a Texas rack and delivered to Louisiana owes Louisiana excise. Without an
 * origin the engine assumes the load never crossed a state line, which is the
 * assumption that quietly attributes excise to the wrong state.
 *
 * TWO WAYS IN, in order of strength:
 *   1. Link to a LIFTING line — authoritative, and it ties the invoice back to
 *      the physical movement a terminal issued a BOL for.
 *   2. Name a TERMINAL directly — for third-party carrier deliveries and loads
 *      keyed in after the fact, where no lifting exists to point at.
 *
 * Refused once the shipment is INVOICED, and the refusal EXPLAINS ITSELF rather
 * than presenting a dead control: the invoice snapshotted where the fuel came
 * from, and changing the shipment now would leave the two disagreeing while the
 * tax stayed as it was.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Link2, Loader2, MapPin, X } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';

interface LiftingCandidate {
  liftingItemId: string;
  liftingNumber: string;
  scheduledDate: string;
  bolNumber: string | null;
  grossGallons: string | number;
  terminal: { id: string; name: string; state: string | null; taxJurisdictionId: string | null } | null;
  alreadyLinked: boolean;
}

export interface ShipmentItemOrigin {
  id: string;
  originTerminal?: { id: string; name: string; state: string | null } | null;
  liftingItem?: {
    id: string;
    lifting: {
      liftingNumber: string;
      terminal: { id: string; name: string; state: string | null } | null;
    };
  } | null;
}

interface Props {
  item: ShipmentItemOrigin;
  shipmentStatus: string;
  /**
   * Whether the shipment sits under an invoice.
   *
   * The server locks on THIS, not on the shipment's status: invoicing only
   * flips DELIVERED / VERIFIED / POSTED shipments to INVOICED, so a status
   * check alone would offer an editable control that the API then refuses.
   */
  invoiceId?: string | null;
  onChanged: () => void;
}

export default function LiftedAtCell({ item, shipmentStatus, invoiceId, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState<LiftingCandidate[]>([]);
  const [terminals, setTerminals] = useState<{ id: string; name: string; state: string | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = Boolean(invoiceId) || shipmentStatus === 'INVOICED';

  const lifted =
    item.liftingItem?.lifting?.terminal ?? item.originTerminal ?? null;
  const viaLifting = Boolean(item.liftingItem);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const [candRes, termRes] = await Promise.all([
        apiFetch(`${API_URL}/api/shipments/items/${item.id}/lifting-candidates`),
        apiFetch(`${API_URL}/api/terminals?limit=200`),
      ]);
      if (candRes.ok) setCandidates((await candRes.json()).candidates ?? []);
      if (termRes.ok) {
        const data = await termRes.json();
        setTerminals(data.items ?? data.terminals ?? []);
      }
    } catch {
      setError('Could not load origin options.');
    } finally {
      setLoading(false);
    }
  }, [item.id]);

  useEffect(() => {
    if (open) loadOptions();
  }, [open, loadOptions]);

  const linkLifting = async (liftingItemId: string | null) => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/shipments/items/${item.id}/lifting`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ liftingItemId }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link');
    } finally {
      setSaving(false);
    }
  };

  const setTerminal = async (terminalId: string | null) => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch(`${API_URL}/api/shipments/items/${item.id}/origin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ terminalId }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set origin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {lifted ? (
        <button
          onClick={() => !locked && setOpen(true)}
          disabled={locked}
          title={
            locked
              ? 'This shipment has been invoiced. The invoice recorded where the fuel came from; changing it now would leave the two disagreeing without correcting the tax.'
              : 'Change where this fuel was lifted'
          }
          className={`inline-flex items-center gap-1.5 text-xs font-mono ${
            locked ? 'text-text-tertiary cursor-not-allowed' : 'text-text-secondary hover:text-amber'
          }`}
        >
          {viaLifting ? <Link2 className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
          {lifted.name}
          {lifted.state ? ` (${lifted.state})` : ''}
        </button>
      ) : (
        <button
          onClick={() => !locked && setOpen(true)}
          disabled={locked}
          title={
            locked
              ? 'This shipment has been invoiced, so its origin can no longer be set. Credit and re-issue the invoice instead.'
              : 'Record where this fuel was lifted'
          }
          className={`inline-flex items-center gap-1.5 text-xs font-mono ${
            locked ? 'text-text-tertiary cursor-not-allowed' : 'text-error-red hover:text-amber'
          }`}
        >
          <AlertTriangle className="w-3 h-3" />
          Origin not set
        </button>
      )}

      {/* An invoiced shipment explains WHY rather than just being dead. */}
      {locked && !lifted && (
        <p className="mt-0.5 text-[10px] text-text-tertiary font-mono max-w-xs">
          Invoiced without an origin — tax was calculated assuming the load stayed in one state.
        </p>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-panel-dark border border-panel-border max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-panel-border">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Where was this lifted?</h3>
                <p className="text-text-tertiary text-xs mt-1">
                  Tax follows the movement. A load lifted in one state and delivered to another owes
                  the destination state — the engine cannot work that out without this.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-text-tertiary hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="m-4 border border-error-red/50 bg-error-red/10 p-3 text-error-red text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 text-amber animate-spin" />
              </div>
            ) : (
              <div className="p-5 space-y-6">
                {/* PREFERRED: a real lifting. */}
                <div>
                  <h4 className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Link to a lifting (preferred)
                  </h4>
                  {candidates.length === 0 ? (
                    <p className="text-text-tertiary text-sm">
                      No liftings for this product around the ship date. Enter the lifting first, or
                      pick a terminal below.
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {candidates.map((candidate) => (
                        <button
                          key={candidate.liftingItemId}
                          onClick={() => linkLifting(candidate.liftingItemId)}
                          disabled={saving}
                          className={`w-full flex items-center justify-between gap-3 border p-3 text-left transition-colors ${
                            item.liftingItem?.id === candidate.liftingItemId
                              ? 'border-amber bg-amber/10'
                              : 'border-panel-border hover:border-amber/60'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="text-text-primary text-sm font-mono">
                              {candidate.liftingNumber}
                              {candidate.terminal && (
                                <span className="text-text-secondary">
                                  {' '}
                                  &middot; {candidate.terminal.name}
                                  {candidate.terminal.state ? ` (${candidate.terminal.state})` : ''}
                                </span>
                              )}
                            </div>
                            <div className="text-text-tertiary text-xs font-mono">
                              {new Date(candidate.scheduledDate).toLocaleDateString()}
                              {candidate.bolNumber && ` · BOL ${candidate.bolNumber}`}
                              {' · '}
                              {Number(candidate.grossGallons).toLocaleString()} gal
                              {candidate.terminal && !candidate.terminal.taxJurisdictionId && (
                                <span className="text-amber">
                                  {' '}
                                  · terminal has no tax jurisdiction
                                </span>
                              )}
                            </div>
                          </div>
                          {candidate.alreadyLinked && (
                            <span className="flex-shrink-0 text-[10px] text-text-tertiary border border-panel-border px-1.5 py-0.5">
                              already used
                            </span>
                          )}
                          {item.liftingItem?.id === candidate.liftingItemId && (
                            <Check className="w-4 h-4 text-amber flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* FALLBACK: a terminal, for loads with no lifting behind them. */}
                <div>
                  <h4 className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Or name a terminal
                  </h4>
                  <p className="text-text-tertiary text-xs mb-2">
                    For a third-party carrier delivery, or a load entered after the fact — where no
                    lifting exists to point at.
                  </p>
                  <select
                    value={item.originTerminal?.id ?? ''}
                    onChange={(e) => setTerminal(e.target.value || null)}
                    disabled={saving}
                    className="w-full bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono text-sm"
                  >
                    <option value="">— none —</option>
                    {terminals.map((terminal) => (
                      <option key={terminal.id} value={terminal.id}>
                        {terminal.name}
                        {terminal.state ? ` (${terminal.state})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {(item.liftingItem || item.originTerminal) && (
                  <button
                    onClick={() => (item.liftingItem ? linkLifting(null) : setTerminal(null))}
                    disabled={saving}
                    className="text-xs text-error-red hover:underline font-mono"
                  >
                    Clear origin
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
